import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { useSeriesWidgetContext } from "~/components/article/article-left-hand-side-widgets/series-widget/contexts";
import { tracking } from "~/data";
import { getAbsoluteUrl } from "~/lib/utils";
import type { seriesWidgetDisplayArticle$key } from "~/queries/__generated__/seriesWidgetDisplayArticle.graphql";
import type { seriesWidgetDisplaySeriesItemResult$key } from "~/queries/__generated__/seriesWidgetDisplaySeriesItemResult.graphql";

import { useTracking } from "./hooks";
import { SeriesWidgetEntrypointHeadline } from "./series-widget-entrypoint-headline";
import { SeriesWidgetItem } from "./series-widget-item";
import { Container, StyledEntityLink } from "./styles";

export type Props = {
  className?: string;
  reference: seriesWidgetDisplayArticle$key;
  seriesItemsReference: seriesWidgetDisplaySeriesItemResult$key;
};

export const SeriesWidgetDisplay: FunctionComponent<Props> = ({
  reference: reference_,
  seriesItemsReference,
}) => {
  const article = useFragment(
    graphql`
      fragment seriesWidgetDisplayArticle on Article {
        ...seriesWidgetEntrypointHeadlineArticle
        entityId
        seriesContainer {
          ...entityLink
        }
      }
    `,
    reference_,
  );

  const seriesItems = useFragment(
    graphql`
      fragment seriesWidgetDisplaySeriesItemResult on SeriesItemResult @relay(plural: true) {
        ... on Article {
          ...seriesWidgetItemContent
          entityId
          urlAlias
        }
      }
    `,
    seriesItemsReference,
  );

  const { captureTrackImpressionEventTargetElement, trackClick } = useTracking();
  const { campaign, currentItemIndex } = useSeriesWidgetContext();

  // the default limit
  const seriesLimit = 10;
  const rangeBeforeCurrent = 4;
  const rangeAfterCurrent = 5;

  const isShowMoreOn = useMemo(
    () => (seriesItems?.length ?? 0) > seriesLimit,
    [seriesItems?.length],
  );

  const itemRange = useMemo<[start: number, end: number]>(() => {
    // when current inside the first 'seriesLimit' items or number of series article is less than seriesLimit
    if (currentItemIndex <= rangeBeforeCurrent || seriesItems.length - 1 < seriesLimit) {
      return [0, seriesLimit];
    }
    // when current inside the last 'seriesLimit' items
    if (currentItemIndex > seriesItems.length - rangeAfterCurrent - 1) {
      return [seriesItems.length - seriesLimit, seriesItems.length];
    }
    // in the middle
    return [currentItemIndex - rangeBeforeCurrent, currentItemIndex + rangeAfterCurrent + 1];
  }, [currentItemIndex, seriesItems.length]);

  const trimmedSeriesItems = useMemo(
    () => (isShowMoreOn ? seriesItems.slice(...itemRange) : seriesItems),
    [seriesItems, isShowMoreOn, itemRange],
  );

  return (
    <Container ref={captureTrackImpressionEventTargetElement}>
      <SeriesWidgetEntrypointHeadline reference={article} total={seriesItems.length} />
      {trimmedSeriesItems.map((seriesItem, index) =>
        seriesItem ? (
          <SeriesWidgetItem
            index={itemRange[0] + index + 1}
            isActive={article.entityId === seriesItem.entityId}
            key={seriesItem.entityId}
            reference={seriesItem}
            onClick={() => {
              trackClick(seriesItem.entityId, getAbsoluteUrl(seriesItem.urlAlias));
            }}
          />
        ) : null,
      )}
      {isShowMoreOn && article.seriesContainer && (
        <StyledEntityLink
          query={{
            campaign,
            module: tracking.module.SeriesLHSMoreOn,
            pgtype: tracking.pageType.Article,
          }}
          reference={article.seriesContainer}
        >
          {">"} More on this Series
        </StyledEntityLink>
      )}
    </Container>
  );
};

SeriesWidgetDisplay.displayName = "SeriesWidgetDisplay";
